import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowRight, Check, Sparkles, Sprout, Zap, ShieldCheck, Landmark, Bot } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Pricing / Upgrade page — 2-column layout (Free vs Pro). Toggle between
 * monthly and annual. Clicking "Subscribe" creates a PayTabs Hosted
 * Payment Page and redirects.
 */
export default function Pricing() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [cycle, setCycle] = useState("annual");
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/billing/config").then(({ data }) => setConfig(data)).catch(() => setConfig(null));
    if (user) api.get("/billing/status").then(({ data }) => setStatus(data)).catch(() => setStatus(null));
    if (params.get("canceled") === "1") toast.info(t("toasts.checkout_canceled"));
  }, [user, params]);

  const currency = config?.currency || "USD";
  const decimals = config?.price_decimals ?? 2;
  const monthlyPrice = config?.monthly_price ?? 9.99;
  const annualPrice = config?.annual_price ?? 79.99;
  const monthlyStr = monthlyPrice.toFixed(decimals);
  const annualStr = annualPrice.toFixed(decimals);
  const annualPerMonthStr = (annualPrice / 12).toFixed(decimals);
  const savePct = Math.round(100 - (annualPrice / (monthlyPrice * 12)) * 100);
  const ready = config?.ready === true;
  // Waitlist triggers on the *user's* preferred currency, not the billing
  // provider currency (which is fixed to OMR while only PayTabs is wired).
  const userCurrency = user?.currency;
  const waitlistCurrency = userCurrency === "INR" || userCurrency === "PKR" ? userCurrency : null;

  const startCheckout = async () => {
    if (!user) { navigate("/?upgrade=1"); return; }
    if (!ready) { toast.error(t("toasts.billing_unavailable")); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout-session", { cycle });
      window.location.assign(data.url);
    } catch (e) {
      const msg = typeof e.response?.data?.detail === "string" ? e.response.data.detail : t("toasts.checkout_start_failed");
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const cancelSubscription = async () => {
    if (!window.confirm("Cancel auto-renewal? You'll keep Pro until the end of the current period.")) return;
    setBusy(true);
    try {
      await api.post("/billing/cancel");
      toast.success(t("toasts.autorenew_off"));
      const { data } = await api.get("/billing/status");
      setStatus(data);
    } catch (e) {
      const msg = typeof e.response?.data?.detail === "string" ? e.response.data.detail : t("toasts.cancel_failed");
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const isPro = status?.is_pro;

  return (
    <div className="min-h-screen bg-[var(--bg-primary)]" data-testid="pricing-page">
      <div className="mx-auto max-w-5xl px-6 py-16">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 label-eyebrow" style={{ color: "var(--accent-primary)" }}>
            <Sprout size={12}/> {t("pricing.eyebrow")}
          </div>
          <h1
            className="mt-3 text-4xl sm:text-5xl font-medium tracking-tight"
            style={{ fontFamily: "var(--font-heading)" }}
            data-testid="pricing-headline"
          >
            {t("pricing.headline")}
          </h1>
          <p className="mt-4 text-base text-[var(--text-secondary)] max-w-xl mx-auto">
            {t("pricing.subtitle")}
          </p>
          {isPro && (
            <div
              className="inline-block mt-6 px-4 py-2 rounded-full text-sm font-medium"
              style={{ background: "rgba(45,90,64,0.10)", color: "var(--accent-primary)", border: "1px solid rgba(45,90,64,0.28)" }}
              data-testid="pricing-current-plan"
            >
              {t("pricing.current_plan", { status: status.status })}
              {status.cancel_at_period_end && ` ${t("pricing.no_auto_renew")}`}
            </div>
          )}
        </div>

        {!isPro && (
          <div className="flex justify-center mb-8" data-testid="pricing-toggle-row">
            <div className="inline-flex rounded-full border border-[var(--border-default)] p-1 bg-[var(--bg-primary)]">
              <button
                onClick={() => setCycle("monthly")}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${cycle==="monthly" ? "bg-[var(--accent-primary)] text-white" : "text-[var(--text-secondary)]"}`}
                data-testid="pricing-toggle-monthly"
              >{t("pricing.monthly")}</button>
              <button
                onClick={() => setCycle("annual")}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${cycle==="annual" ? "bg-[var(--accent-primary)] text-white" : "text-[var(--text-secondary)]"}`}
                data-testid="pricing-toggle-annual"
              >
                {t("pricing.annual")} · <span className="text-[var(--accent-warm)]">{t("pricing.save_pct", { pct: savePct })}</span>
              </button>
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6" data-testid="pricing-cards">
          {/* FREE */}
          <div className="card-flat p-8" data-testid="pricing-card-free">
            <div className="label-eyebrow">{t("pricing.free_plan")}</div>
            <div className="mt-3 text-3xl font-medium" style={{ fontFamily: "var(--font-heading)" }}>{t("pricing.free_price", { currency })}</div>
            <p className="mt-2 text-sm text-[var(--text-secondary)]">{t("pricing.free_perks_title")}</p>
            <div className="mt-6 h-px bg-[var(--border-default)]"/>
            <ul className="mt-6 space-y-3 text-sm text-[var(--text-primary)]">
              <Feat><Landmark size={14}/> {t("pricing.feat_free_1")}</Feat>
              <Feat><Bot size={14}/> {t("pricing.feat_free_2")}</Feat>
              <Feat><Sparkles size={14}/> {t("pricing.feat_free_3")}</Feat>
              <Feat><ShieldCheck size={14}/> {t("pricing.feat_free_4")}</Feat>
            </ul>
          </div>

          {/* PRO */}
          <div
            className="card-flat p-8 relative overflow-hidden"
            data-testid="pricing-card-pro"
            style={{ borderColor: "rgba(45,90,64,0.35)", background: "linear-gradient(155deg, rgba(45,90,64,0.06) 0%, rgba(224,158,80,0.05) 100%)" }}
          >
            <div className="absolute top-4 right-5 text-[10px] tracking-widest uppercase font-semibold px-2 py-0.5 rounded-full"
              style={{ background: "rgba(224,158,80,0.2)", color: "#A16A25" }}>
              {t("pricing.best_value")}
            </div>
            <div className="label-eyebrow" style={{ color: "var(--accent-primary)" }}>{t("pricing.pro_plan")}</div>
            <div className="mt-3 text-3xl font-medium" style={{ fontFamily: "var(--font-heading)" }} data-testid="pricing-price">
              {cycle === "monthly"
                ? t("pricing.pro_price_monthly", { price: monthlyStr, currency })
                : t("pricing.pro_price_annual_split", { monthly: annualPerMonthStr, currency, annual: annualStr })}
            </div>
            <p className="mt-2 text-sm text-[var(--text-secondary)]">{t("pricing.everything_free_plus")}</p>
            <div className="mt-6 h-px bg-[var(--border-default)]"/>
            <ul className="mt-6 space-y-3 text-sm text-[var(--text-primary)]">
              <Feat pro><Landmark size={14}/> {t("pricing.feat_pro_unlimited_banks")}</Feat>
              <Feat pro><Bot size={14}/> {t("pricing.feat_pro_unlimited_ai")}</Feat>
              <Feat pro><Zap size={14}/> {t("pricing.feat_pro_priority_sync")}</Feat>
              <Feat pro><ShieldCheck size={14}/> {t("pricing.feat_pro_priority_support")}</Feat>
            </ul>

            {isPro ? (
              <button
                className="btn-primary w-full justify-center mt-8"
                disabled={busy || status?.cancel_at_period_end}
                onClick={cancelSubscription}
                data-testid="pricing-manage-btn"
              >
                {busy ? "…" : status?.cancel_at_period_end ? t("pricing.cta_renewal_off") : t("pricing.cta_manage")}
              </button>
            ) : (
              <button
                className="btn-primary w-full justify-center mt-8"
                disabled={busy}
                onClick={startCheckout}
                data-testid="pricing-cta-btn"
              >
                {busy ? "…" : user ? t("pricing.cta_subscribe", { cycle: t(`pricing.${cycle}`) }) : t("pricing.cta_signin")}
                <ArrowRight size={15}/>
              </button>
            )}
            <div className="mt-3 text-[11px] text-[var(--text-muted)] text-center">
              {t("pricing.payment_methods")}
            </div>
          </div>
        </div>

        <div className="mt-10 text-center">
          <Link to={user ? "/dashboard" : "/"} className="text-sm text-[var(--text-muted)] hover:underline">
            ← {user ? t("pricing.back_to_dashboard") : t("pricing.back_to_home")}
          </Link>
        </div>

        {waitlistCurrency && (
          <WaitlistCard currency={waitlistCurrency} t={t} />
        )}
      </div>
    </div>
  );
}

function WaitlistCard({ currency, t }) {
  const [email, setEmail] = useState("");
  const [provider, setProvider] = useState("any");
  const [busy, setBusy] = useState(false);
  const [joined, setJoined] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/waitlist/payments/stats").then(({ data }) => setStats(data)).catch(() => setStats(null));
  }, []);

  const providers = currency === "INR"
    ? [
        { key: "razorpay", label: "Razorpay" },
        { key: "any", label: t("pricing.waitlist.any") },
      ]
    : [
        { key: "jazzcash", label: "JazzCash" },
        { key: "easypaisa", label: "Easypaisa" },
        { key: "any", label: t("pricing.waitlist.any") },
      ];

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error(t("pricing.waitlist.email_invalid"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/waitlist/payments", { email, currency, preferred_provider: provider });
      setJoined(true);
      toast.success(t("pricing.waitlist.joined_toast"));
      api.get("/waitlist/payments/stats").then(({ data }) => setStats(data)).catch(() => {});
    } catch (err) {
      const msg = typeof err.response?.data?.detail === "string" ? err.response.data.detail : t("pricing.waitlist.failed_toast");
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const count = stats?.[currency] || 0;

  return (
    <div className="mt-12 card-flat p-8" data-testid="pricing-waitlist-card"
      style={{ background: "linear-gradient(150deg, rgba(224,158,80,0.06) 0%, rgba(45,90,64,0.04) 100%)", borderColor: "rgba(224,158,80,0.28)" }}>
      <div className="label-eyebrow" style={{ color: "var(--accent-warm)" }} data-testid="waitlist-eyebrow">
        {t("pricing.waitlist.eyebrow", { currency })}
      </div>
      <h2 className="mt-3 text-2xl sm:text-3xl font-medium tracking-tight" style={{ fontFamily: "var(--font-heading)" }} data-testid="waitlist-title">
        {t("pricing.waitlist.title")}
      </h2>
      <p className="mt-3 text-sm text-[var(--text-secondary)] max-w-2xl" data-testid="waitlist-body">
        {t("pricing.waitlist.body", { currency })}
      </p>
      {count > 0 && (
        <p className="mt-2 text-xs text-[var(--text-muted)]" data-testid="waitlist-count">
          {t("pricing.waitlist.count", { count })}
        </p>
      )}

      {joined ? (
        <div className="mt-6 p-4 rounded-lg text-sm" data-testid="waitlist-success"
          style={{ background: "rgba(45,90,64,0.08)", color: "var(--accent-primary)", border: "1px solid rgba(45,90,64,0.28)" }}>
          {t("pricing.waitlist.success_body")}
        </div>
      ) : (
        <form onSubmit={submit} className="mt-6 flex flex-col sm:flex-row gap-3" data-testid="waitlist-form">
          <input
            type="email"
            required
            placeholder={t("pricing.waitlist.email_placeholder")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="field-input flex-1"
            data-testid="waitlist-email-input"
          />
          <select
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
            className="field-input sm:w-40"
            data-testid="waitlist-provider-select"
          >
            {providers.map((p) => (
              <option key={p.key} value={p.key}>{p.label}</option>
            ))}
          </select>
          <button
            type="submit"
            disabled={busy}
            className="btn-primary justify-center"
            data-testid="waitlist-submit-btn"
          >
            {busy ? "…" : t("pricing.waitlist.notify_cta")}
          </button>
        </form>
      )}
    </div>
  );
}

function Feat({ children, pro }) {
  return (
    <li className="flex items-center gap-2.5">
      <Check size={14} className={pro ? "text-[var(--accent-primary)]" : "text-[var(--text-muted)]"}/>
      <span className="text-sm leading-snug flex items-center gap-2">{children}</span>
    </li>
  );
}
